import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HomepageHero from "@/components/HomepageHero";
import InFeedEditorial from "@/components/InFeedEditorial";
import SidebarFeature from "@/components/SidebarFeature";
import { api, timeAgo, showToast } from "@/lib/api";
import { ShareStoryModal, StartTopicModal, AddCityModal, AddTagModal, expBadge, rolePill } from "@/components/Modals";
import { useAuth } from "@/App";

const PostCard = ({ p, onClick }) => {
  const eb = expBadge(p.experience);
  const rp = rolePill(p.author_role, p.is_anonymous);
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const onShareClick = (e) => { e.stopPropagation(); setShareOpen(s=>!s); };

  return (
    <div className="dcard" data-testid={`post-card-${p.id}`} onClick={()=>navigate(`/post/${p.id}`)}>
      {eb && <div className={`dcard-exp-badge ${eb.cls}`}>{eb.label}</div>}
      <div className="dcard-top">
        <div className="dcard-av" style={{background:p.is_anonymous?"#F0EEE9":"#EBE8F7", color:p.is_anonymous?"#6C6C70":"#6050C8"}}>
          {p.display_initials || "?"}
        </div>
        <div className="dcard-meta">
          <div className="dcard-r1">
            <span className="dcard-author">{p.display_author}</span>
            {rp && <span className={`rpill ${rp.cls}`}>{rp.label}</span>}
            {p.topic_chip && <span className="topic-chip">{p.topic_chip}</span>}
          </div>
          <div className="dcard-loc">📍 {p.city}{p.area ? `, ${p.area}`:""}{p.society ? ` · ${p.society}`:""}</div>
        </div>
        <div className="dcard-time">{timeAgo(p.created_at)}</div>
      </div>
      <div className="dcard-title">{p.title}</div>
      <div className="dcard-body">{p.body}</div>
      <div className="dcard-tags">
        <span className="dtag city">{p.city}</span>
        {p.topic_chip && <span className="dtag custom">{p.topic_chip}</span>}
        {(p.tags||[]).slice(0,3).map((t,i)=> <span key={i} className="dtag">{t}</span>)}
      </div>
      <div className="dcard-footer">
        <button className="daction" data-testid={`like-${p.id}`} onClick={(e)=>{e.stopPropagation();}}>👍 {p.likes||0}</button>
        <button className="daction">💬 {p.comments_count||0}</button>
        <button className="daction">👁 {p.views||0}</button>
        <div style={{marginLeft:"auto",position:"relative"}}>
          <button className="daction" onClick={onShareClick}>🔗 Share</button>
          {shareOpen && (
            <div className="share-popup" onClick={(e)=>e.stopPropagation()}>
              {[
                ["💬","WhatsApp",`https://wa.me/?text=${encodeURIComponent(p.title+" - "+window.location.origin+"/post/"+p.id)}`],
                ["🐦","X / Twitter",`https://twitter.com/intent/tweet?text=${encodeURIComponent(p.title)}&url=${encodeURIComponent(window.location.origin+"/post/"+p.id)}`],
                ["💼","LinkedIn",`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin+"/post/"+p.id)}`],
                ["📘","Facebook",`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin+"/post/"+p.id)}`],
              ].map(([i,l,h])=>(
                <a key={l} className="sp-item" href={h} target="_blank" rel="noreferrer"><span className="sp-icon">{i}</span>Share on {l}</a>
              ))}
              <div className="sp-item" onClick={()=>{ navigator.clipboard.writeText(window.location.origin+"/post/"+p.id); showToast("Link copied"); setShareOpen(false); }}>
                <span className="sp-icon">📋</span>Copy Link
              </div>
            </div>
          )}
        </div>
        <button className="daction" data-testid={`flag-${p.id}`} onClick={async(e)=>{e.stopPropagation(); try{ await api.post(`/posts/${p.id}/flag`,{reason:""}); showToast("Reported. Thanks."); }catch{ showToast("Sign in to flag"); }}}>🚩</button>
      </div>
    </div>
  );
};

export default function Forum() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const cityParam = searchParams.get("city") || "";
  const [city, setCity] = useState(cityParam || "All");
  const [topic, setTopic] = useState("All");
  const [exp, setExp] = useState("All");
  const [sort, setSort] = useState("recent");

  // Sync city from URL whenever it changes (e.g. hero CTA navigates to /?city=Mumbai)
  useEffect(() => {
    if (cityParam && cityParam !== city) setCity(cityParam);
    if (!cityParam && city !== "All" && !searchParams.has("city")) {
      // only auto-reset if URL has been explicitly cleared
    }
  }, [cityParam]); // eslint-disable-line
  const [posts, setPosts] = useState([]);
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({cities:[], total_posts:0, total_members:0});
  const [articles, setArticles] = useState([]);
  const [editorial, setEditorial] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [moreTopicsOpen, setMoreTopicsOpen] = useState(false);
  const [moreTopicsAnchor, setMoreTopicsAnchor] = useState({ top: 0, left: 0 });
  const moreTopicsRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (moreTopicsRef.current && !moreTopicsRef.current.contains(e.target)) {
        setMoreTopicsOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const loadPosts = async () => {
    const params = { sort };
    if (city !== "All") params.city = city;
    if (topic !== "All") params.topic = topic;
    if (exp !== "All") params.experience = exp;
    if (q) params.q = q;
    const r = await api.get("/posts", { params });
    setPosts(r.data || []);
  };

  useEffect(() => { loadPosts(); }, [city, topic, exp, sort, q]); // eslint-disable-line
  useEffect(() => {
    api.get("/cities").then(r=>setCities(r.data||[]));
    api.get("/tags").then(r=>setTags(r.data||[]));
    api.get("/stats").then(r=>setStats(r.data));
    api.get("/articles").then(r=>setArticles((r.data||[]).slice(0,4)));
    api.get("/media/infeed_editorial").then(r=>setEditorial(r.data||[]));
    // Track homepage visit (fire and forget)
    api.post("/track/visit").catch(()=>{});
  }, []);

  const onShare = () => { if (!user) { login(); return; } setShareOpen(true); };
  const onTopic = () => { if (!user) { login(); return; } setTopicOpen(true); };

  return (
    <div>
      <Navbar onShare={onShare} onTopic={onTopic} />

      {/* SURVEY BANNER (above Community Notice) */}
      <a className="lt-survey-banner" href="https://forms.gle/BnngD4XPsApUfvak8"
         target="_blank" rel="noreferrer" data-testid="survey-banner">
        <span className="lt-sb-icon">✨</span>
        <div className="lt-sb-text">
          <span className="lt-sb-eyebrow">LANTEN SURVEY</span>
          <span className="lt-sb-title">Click to take our 90-second survey — your inputs will help millions of landlords &amp; tenants</span>
        </div>
        <span className="lt-sb-arrow">Take survey →</span>
      </a>

      {/* COMMUNITY NOTICE (prominent, above hero) */}
      <div className="lt-community-notice" data-testid="community-notice">
        <div className="lt-cn-icon">📣</div>
        <div className="lt-cn-body">
          <div className="lt-cn-eyebrow">COMMUNITY NOTICE</div>
          <div className="lt-cn-title">This is a space for real experiences — not listings</div>
          <div className="lt-cn-sub">Share honest rental stories. Help others make better decisions. All posts reviewed before going live. Anonymous posting is allowed. Be truthful, be respectful.</div>
        </div>
      </div>

      {/* SPLIT HERO: left prompts + right cinematic carousel */}
      <div className="lt-hero-split">
        <div className="lt-prompts">
          <div className="lt-prompts-eyebrow">PARTICIPATE</div>
          <h2 className="lt-prompts-headline">Tell the community,<br/>help the community!</h2>
          <div className="lt-prompts-sub">Pick a prompt below to share your story →</div>
          <div className="lt-prompt-grid">
            <button className="lt-prompt-card" data-testid="prompt-recent-dwelling" onClick={onShare}>
              <span className="lt-prompt-icon">🏠</span>
              <span className="lt-prompt-text">How was your experience at your most recent dwelling?</span>
              <span className="lt-prompt-cta">Share →</span>
            </button>
            <button className="lt-prompt-card" data-testid="prompt-favorite-landlord" onClick={onShare}>
              <span className="lt-prompt-icon">⭐</span>
              <span className="lt-prompt-text">Who was your favourite landlord and why?</span>
              <span className="lt-prompt-cta">Share →</span>
            </button>
            <button className="lt-prompt-card" data-testid="prompt-great-tenant" onClick={onShare}>
              <span className="lt-prompt-icon">🔑</span>
              <span className="lt-prompt-text">As a landlord, what makes for a great tenant?</span>
              <span className="lt-prompt-cta">Share →</span>
            </button>
          </div>
        </div>
        <HomepageHero />
      </div>

      {/* CITY STRIP */}
      <div className="cstrip">
        <div className="cstrip-i">
          <div data-testid="city-all" className={`cpill ${city==="All"?"on":""}`} onClick={()=>setCity("All")}>🌍 All Cities</div>
          <div className="csep"></div>
          {cities.map(c => (
            <div key={c.name} data-testid={`city-${c.name}`} className={`cpill ${city===c.name?"on":""}`} onClick={()=>setCity(c.name)}>{c.name}</div>
          ))}
          <div className="csep"></div>
          <div className="city-add-btn" data-testid="city-add-btn" onClick={()=>{ if(!user){ login(); return; } setCityOpen(true); }}>＋ Add City</div>
        </div>
      </div>

      {/* TOPIC STRIP */}
      <div className="tstrip">
        <div className="tstrip-i">
          <div className="tstrip-label">Browse Topics</div>
          <div data-testid="topic-all" className={`tpill ${topic==="All"?"on":""}`} onClick={()=>setTopic("All")}>All Topics</div>
          {tags.slice(0, 5).map(t => (
            <div key={t.name} data-testid={`topic-${t.name}`} className={`tpill ${topic===t.name?"on":""}`} onClick={()=>setTopic(t.name)}>{t.name}</div>
          ))}
          {tags.length > 5 && (
            <div ref={moreTopicsRef} style={{position:"relative"}}>
              <div data-testid="topic-more-toggle"
                   className={`tpill ${tags.slice(5).some(t => t.name === topic) ? "on" : ""}`}
                   style={{borderColor:"var(--gold)",color: tags.slice(5).some(t => t.name === topic) ? "white" : "var(--gold)",
                           background: tags.slice(5).some(t => t.name === topic) ? "var(--gold)" : "transparent",
                           display:"flex",alignItems:"center",gap:4,fontWeight:700}}
                   onClick={(e)=>{
                     e.stopPropagation();
                     const rect = e.currentTarget.getBoundingClientRect();
                     setMoreTopicsAnchor({ top: rect.bottom + 6, left: rect.left });
                     setMoreTopicsOpen(v=>!v);
                   }}>
                More topics
                <span style={{transform: moreTopicsOpen?"rotate(180deg)":"none",transition:"transform .2s",fontSize:10,marginLeft:2}}>▾</span>
              </div>
            </div>
          )}
          <div className="topic-add-btn" data-testid="topic-add-btn" onClick={()=>{ if(!user){ login(); return; } setTagOpen(true); }}>＋ Create Tag</div>
        </div>
      </div>

      {/* Floating panel rendered outside the overflow container */}
      {moreTopicsOpen && tags.length > 5 && (
        <div data-testid="topic-more-panel"
             onClick={(e)=>e.stopPropagation()}
             style={{position:"fixed",top:moreTopicsAnchor.top,left:moreTopicsAnchor.left,minWidth:240,
                     background:"white",border:"1.5px solid var(--border)",borderRadius:12,padding:6,zIndex:1000,
                     boxShadow:"0 8px 32px rgba(0,0,0,.1),0 2px 8px rgba(0,0,0,.06)",
                     maxHeight:340,overflowY:"auto"}}>
          <div style={{fontSize:9.5,textTransform:"uppercase",letterSpacing:2,fontWeight:700,
                       color:"var(--ink4)",padding:"6px 10px 8px",borderBottom:"1px solid var(--bg2)"}}>
            More topics ({tags.length - 5})
          </div>
          {tags.slice(5).map(t => (
            <div key={t.name} data-testid={`more-topic-${t.name}`}
                 onClick={()=>{ setTopic(t.name); setMoreTopicsOpen(false); }}
                 style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                         padding:"8px 10px",borderRadius:7,cursor:"pointer",fontSize:13,
                         fontWeight:topic===t.name?700:500,
                         color:topic===t.name?"var(--gold)":"var(--ink2)",
                         background:topic===t.name?"var(--gold-bg)":"transparent"}}
                 onMouseEnter={(e)=>{ if(topic!==t.name) e.currentTarget.style.background = "var(--bg)"; }}
                 onMouseLeave={(e)=>{ if(topic!==t.name) e.currentTarget.style.background = "transparent"; }}>
              <span>{t.name}</span>
              <span style={{fontSize:11,color:"var(--ink4)",fontFamily:"'JetBrains Mono',monospace"}}>{t.posts_count || 0}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flayout">
        <main>
          <div className="ftbar">
            <div className="fthead">
              {q ? <>Search results for "<em style={{color:"#C47E2B"}}>{q}</em>"</> : "Discussions"}
              <span data-testid="feed-count">({posts.length})</span>
              {q && (
                <button onClick={()=>navigate("/")}
                  style={{marginLeft:10,padding:"3px 10px",borderRadius:100,border:"1px solid var(--border2)",
                          background:"transparent",fontSize:11,fontWeight:600,color:"var(--ink3)",cursor:"pointer",fontFamily:"inherit"}}>
                  ✕ Clear search
                </button>
              )}
            </div>
            <div className="sortrow">
              <div className={`sbtn ${sort==="recent"?"on":""}`} onClick={()=>setSort("recent")}>Recent</div>
              <div className={`sbtn ${sort==="most_helpful"?"on":""}`} onClick={()=>setSort("most_helpful")}>Most Helpful</div>
              <div className={`sbtn ${sort==="most_active"?"on":""}`} onClick={()=>setSort("most_active")}>Most Active</div>
            </div>
          </div>

          {/* Experience filter */}
          <div className="sortrow" style={{marginBottom:14}}>
            {[["All","All Experiences"],["positive","😊 Positive"],["mixed","🤔 Mixed"],["negative","😤 Negative"]].map(([k,l])=>(
              <div key={k} data-testid={`exp-filter-${k}`} className={`sbtn ${exp===k?"on":""}`} onClick={()=>setExp(k)}>{l}</div>
            ))}
          </div>

          {posts.length === 0 ? (
            <div className="empty-feed">
              <div style={{fontSize:38,marginBottom:11}}>💭</div>
              <p style={{fontSize:16,fontWeight:600,color:"#1C1C1E",marginBottom:6}}>No stories found</p>
              <p style={{fontSize:14}}>Try a different filter or be the first to share!</p>
            </div>
          ) : posts.map((p, i) => (
            <React.Fragment key={p.id}>
              <PostCard p={p} />
              {((i + 1) % 6 === 0) && editorial[Math.floor(i / 6) % editorial.length] && (
                <InFeedEditorial item={editorial[Math.floor(i / 6) % editorial.length]} />
              )}
            </React.Fragment>
          ))}
        </main>

        <aside>
          <div className="sw">
            <div className="sw-title">Community Stats</div>
            <div className="stat-2">
              <div className="sbox"><div className="sn">{stats.total_posts}</div><div className="sl">Stories</div></div>
              <div className="sbox"><div className="sn">{stats.total_members}</div><div className="sl">Members</div></div>
            </div>
          </div>

          <SidebarFeature />

          <div className="sw">
            <div className="sw-title">Top Cities</div>
            <div className="city-list">
              {(stats.cities||[]).slice(0,8).map(c => (
                <div key={c.name} className={`city-row ${city===c.name?"on":""}`} onClick={()=>setCity(c.name)}>
                  <span className="cn">{c.name}</span>
                  <span className="cc">{c.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sw">
            <div className="sw-title">LANTEN Reads</div>
            {articles.map(a => (
              <div key={a.id} className="article-card" onClick={()=>navigate(`/reads/${a.id}`)}>
                <div className="art-tag">{a.tag}</div>
                <div className="art-title">{a.title}</div>
                <div className="art-meta">{timeAgo(a.created_at)} · {a.author}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <ShareStoryModal open={shareOpen} onClose={()=>setShareOpen(false)} onSubmitted={loadPosts} />
      <StartTopicModal open={topicOpen} onClose={()=>setTopicOpen(false)} onSubmitted={loadPosts} />
      <AddCityModal open={cityOpen} onClose={()=>setCityOpen(false)} onAdded={()=>api.get("/cities").then(r=>setCities(r.data||[]))} />
      <AddTagModal open={tagOpen} onClose={()=>setTagOpen(false)} onAdded={()=>api.get("/tags").then(r=>setTags(r.data||[]))} />
    </div>
  );
}
